/**
 * Preset taxonomy for the directory. Two user-facing levels, aligned to the
 * 2026 AI-application map from YC / a16z / Sequoia:
 *
 *   category    = domain umbrella  (Image, Video, Audio, Text, Code, Agents, …)
 *   sub-category = concrete tool type under a category (Photo, Video Generator, …)
 *   use case / tag = cross-domain task (Generate, Edit, Summarize, …)
 *   collection  = editorial picks
 *
 * Every name is a single atomic concept — no "X & Y" compound labels. Category
 * names never collide with sub-category names, and sub-categories never
 * duplicate the bare category keyword (the category's word may appear only as
 * part of a more specific subtype, e.g. "Video Generator" under "Video").
 *
 * Internal DB `taxonomy.type` stays `group` / `category` / `tag` / `collection`
 * (an implementation detail); the public wording is Category / Sub-category.
 * Counts are live from `getHomeRail()` (src/modules/directory/rail.ts).
 */

import type { LucideIcon } from 'lucide-react';
import {
  AlignLeft,
  AudioLines,
  Banknote,
  Boxes,
  BarChart3,
  Bot,
  Braces,
  Briefcase,
  BriefcaseBusiness,
  Brush,
  Building2,
  Cable,
  CalendarDays,
  Camera,
  Clapperboard,
  Code2,
  Copy,
  Database,
  FileText,
  Film,
  Gamepad2,
  Globe2,
  GraduationCap,
  Handshake,
  HeartPulse,
  Image as ImageIcon,
  Landmark,
  Languages,
  Laptop,
  LayoutDashboard,
  LifeBuoy,
  ListTodo,
  Mail,
  Maximize2,
  Megaphone,
  Mic,
  Monitor,
  MonitorSmartphone,
  Music,
  Newspaper,
  NotebookPen,
  Palette,
  PenLine,
  PenTool,
  Plug,
  Presentation,
  Puzzle,
  Rocket,
  Scale,
  ShieldCheck,
  Search,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Sun,
  Table,
  Terminal,
  Type,
  UserRound,
  Users,
  Video as VideoIcon,
  Workflow,
  Wrench,
  Zap,
} from 'lucide-react';
import type { HomeRailData } from '@/modules/directory/rail';

export type TileColor =
  | 'rose'
  | 'violet'
  | 'sky'
  | 'amber'
  | 'emerald'
  | 'cyan'
  | 'fuchsia'
  | 'indigo'
  | 'lime'
  | 'orange';

export type PresetEntry = {
  slug: string;
  labelEn: string;
  labelZh: string;
  icon: LucideIcon;
  color: TileColor;
};

/** Category = domain umbrella (taxonomy.type = 'group' internally). */
export type CategoryPreset = PresetEntry;

/** Sub-category = concrete tool type (taxonomy.type = 'category' internally). */
export type SubCategoryPreset = PresetEntry & {
  /** The category (domain) this sub-category hangs under. */
  categorySlug: string;
};

/** Visual identity (label fallback, icon, hue) per production taxonomy group.
 *  Slugs are the real `group` rows in the `taxonomy` table — every
 *  `/category/<slug>` resolves. Unknown groups get `FALLBACK_CATEGORY_LOOK`. */
export const CATEGORY_PRESETS: CategoryPreset[] = [
  { slug: 'image', labelEn: 'Image', labelZh: '图片', icon: ImageIcon, color: 'sky' },
  { slug: 'video', labelEn: 'Video', labelZh: '视频', icon: VideoIcon, color: 'rose' },
  { slug: 'voice-audio', labelEn: 'Audio', labelZh: '语音音频', icon: AudioLines, color: 'fuchsia' },
  { slug: 'writing-text', labelEn: 'Text', labelZh: '写作文本', icon: Type, color: 'amber' },
  { slug: 'coding-development', labelEn: 'Coding', labelZh: '编程开发', icon: Code2, color: 'emerald' },
  { slug: 'productivity', labelEn: 'Productivity', labelZh: '效率', icon: Zap, color: 'lime' },
  { slug: 'business', labelEn: 'Business', labelZh: '商务', icon: BriefcaseBusiness, color: 'orange' },
  { slug: 'marketing-advertising', labelEn: 'Marketing', labelZh: '营销广告', icon: Megaphone, color: 'cyan' },
  { slug: 'education-learning', labelEn: 'Education', labelZh: '教育学习', icon: GraduationCap, color: 'indigo' },
  { slug: 'life-assistant', labelEn: 'Life Assistant', labelZh: '生活助手', icon: UserRound, color: 'violet' },
  { slug: 'ai-detection', labelEn: 'AI Detection', labelZh: 'AI 检测', icon: ShieldCheck, color: 'rose' },
  { slug: 'entertainment', labelEn: 'Entertainment', labelZh: '娱乐', icon: Gamepad2, color: 'fuchsia' },
  { slug: 'other', labelEn: 'Other', labelZh: '其他', icon: Boxes, color: 'sky' },
];

const CATEGORY_LOOK_BY_SLUG = new Map(CATEGORY_PRESETS.map((p) => [p.slug, p]));

/** Icon + hue for a group slug; a neutral generic mark when the slug is unknown. */
export function categoryLook(slug: string): { icon: LucideIcon; color: TileColor } {
  const preset = CATEGORY_LOOK_BY_SLUG.get(slug);
  return { icon: preset?.icon ?? Boxes, color: preset?.color ?? 'sky' };
}

/** Concrete tool types. Slugs must exist in the `taxonomy` table (seeded) so
 *  each `/sub-category/{slug}` route resolves. */
export const SUB_CATEGORY_PRESETS: SubCategoryPreset[] = [
  // Image
  { slug: 'ai-photo-tools', labelEn: 'Photo', labelZh: '图片', categorySlug: 'image', icon: Camera, color: 'sky' },
  { slug: 'ai-design-generator', labelEn: 'Design', labelZh: '设计', categorySlug: 'image', icon: Palette, color: 'violet' },
  { slug: 'ai-art-generator', labelEn: 'Art', labelZh: '艺术', categorySlug: 'image', icon: Brush, color: 'fuchsia' },
  { slug: 'ai-avatars', labelEn: 'Avatars', labelZh: '头像', categorySlug: 'image', icon: Users, color: 'indigo' },
  // Video
  { slug: 'ai-video-generator', labelEn: 'Video Generator', labelZh: '视频生成', categorySlug: 'video', icon: Clapperboard, color: 'rose' },
  { slug: 'ai-video-editor', labelEn: 'Video Editor', labelZh: '视频编辑', categorySlug: 'video', icon: Film, color: 'orange' },
  { slug: 'ai-video-upscaler', labelEn: 'Video Upscaler', labelZh: '视频增强', categorySlug: 'video', icon: Maximize2, color: 'sky' },
  { slug: 'ai-video-translation', labelEn: 'Video Translation', labelZh: '视频翻译', categorySlug: 'video', icon: Languages, color: 'indigo' },
  // Audio
  { slug: 'text-to-speech', labelEn: 'Text to Speech', labelZh: '文字转语音', categorySlug: 'voice-audio', icon: Mic, color: 'indigo' },
  { slug: 'ai-audio', labelEn: 'Audio Generation', labelZh: '音频生成', categorySlug: 'voice-audio', icon: AudioLines, color: 'fuchsia' },
  { slug: 'ai-music', labelEn: 'Music', labelZh: '音乐', categorySlug: 'voice-audio', icon: Music, color: 'amber' },
  { slug: 'ai-transcription', labelEn: 'Transcription', labelZh: '转写', categorySlug: 'voice-audio', icon: FileText, color: 'sky' },
  // Text
  { slug: 'ai-writing', labelEn: 'Writing', labelZh: '写作', categorySlug: 'writing-text', icon: PenLine, color: 'amber' },
  { slug: 'ai-copywriting', labelEn: 'Copywriting', labelZh: '文案', categorySlug: 'writing-text', icon: Copy, color: 'sky' },
  { slug: 'ai-translation', labelEn: 'Translation', labelZh: '翻译', categorySlug: 'writing-text', icon: Languages, color: 'indigo' },
  { slug: 'ai-summarization', labelEn: 'Summarization', labelZh: '摘要', categorySlug: 'writing-text', icon: AlignLeft, color: 'violet' },
  // Data
  { slug: 'ai-search', labelEn: 'Search', labelZh: '搜索', categorySlug: 'productivity', icon: Search, color: 'sky' },
  { slug: 'ai-analysis', labelEn: 'Analysis', labelZh: '分析', categorySlug: 'productivity', icon: BarChart3, color: 'emerald' },
  { slug: 'ai-notebooks', labelEn: 'Notebooks', labelZh: '数据笔记', categorySlug: 'productivity', icon: Table, color: 'violet' },
  { slug: 'ai-dashboards', labelEn: 'Dashboards', labelZh: '数据看板', categorySlug: 'productivity', icon: LayoutDashboard, color: 'cyan' },
  // Code
  { slug: 'ai-code', labelEn: 'Code Assistant', labelZh: '代码助手', categorySlug: 'coding-development', icon: Code2, color: 'emerald' },
  { slug: 'ai-nocode', labelEn: 'No-Code', labelZh: '无代码', categorySlug: 'coding-development', icon: Wrench, color: 'lime' },
  { slug: 'ai-api', labelEn: 'API Tools', labelZh: 'API 工具', categorySlug: 'coding-development', icon: Cable, color: 'cyan' },
  { slug: 'ai-mcp', labelEn: 'MCP Tools', labelZh: 'MCP 工具', categorySlug: 'coding-development', icon: Plug, color: 'orange' },
  // Agents
  { slug: 'ai-agents', labelEn: 'Agent Builders', labelZh: '智能体构建', categorySlug: 'coding-development', icon: Bot, color: 'cyan' },
  { slug: 'ai-vertical-agents', labelEn: 'Vertical Agents', labelZh: '垂直智能体', categorySlug: 'coding-development', icon: BriefcaseBusiness, color: 'orange' },
  { slug: 'ai-automation', labelEn: 'Workflow Automation', labelZh: '工作流自动化', categorySlug: 'coding-development', icon: Workflow, color: 'lime' },
  { slug: 'ai-personal-agents', labelEn: 'Personal Agents', labelZh: '个人智能体', categorySlug: 'coding-development', icon: UserRound, color: 'violet' },
  // Productivity
  { slug: 'ai-productivity-tools', labelEn: 'Task Management', labelZh: '任务管理', categorySlug: 'productivity', icon: ListTodo, color: 'emerald' },
  { slug: 'ai-meetings', labelEn: 'Meetings', labelZh: '会议', categorySlug: 'productivity', icon: Presentation, color: 'rose' },
  { slug: 'ai-notes', labelEn: 'Notes', labelZh: '笔记', categorySlug: 'productivity', icon: NotebookPen, color: 'violet' },
  { slug: 'ai-email', labelEn: 'Email', labelZh: '邮件', categorySlug: 'productivity', icon: Mail, color: 'sky' },
  { slug: 'ai-calendar', labelEn: 'Calendar', labelZh: '日历', categorySlug: 'productivity', icon: CalendarDays, color: 'amber' },
  // Business
  { slug: 'ai-marketing', labelEn: 'Marketing', labelZh: '营销', categorySlug: 'business', icon: Megaphone, color: 'orange' },
  { slug: 'ai-sales', labelEn: 'Sales', labelZh: '销售', categorySlug: 'business', icon: Handshake, color: 'emerald' },
  { slug: 'ai-finance', labelEn: 'Finance', labelZh: '金融', categorySlug: 'business', icon: Banknote, color: 'lime' },
  { slug: 'ai-support', labelEn: 'Support', labelZh: '客服', categorySlug: 'business', icon: LifeBuoy, color: 'sky' },
  { slug: 'ai-legal', labelEn: 'Legal', labelZh: '法律', categorySlug: 'business', icon: Scale, color: 'violet' },
  // Personal
  { slug: 'ai-assistants', labelEn: 'Assistants', labelZh: '助手', categorySlug: 'life-assistant', icon: Sparkles, color: 'violet' },
  { slug: 'ai-health', labelEn: 'Health', labelZh: '健康', categorySlug: 'life-assistant', icon: HeartPulse, color: 'rose' },
  { slug: 'ai-education', labelEn: 'Education', labelZh: '教育', categorySlug: 'life-assistant', icon: GraduationCap, color: 'indigo' },
  { slug: 'ai-lifestyle', labelEn: 'Lifestyle', labelZh: '生活', categorySlug: 'life-assistant', icon: Sun, color: 'amber' },
];

/** Platform signal panel — every platform we track, always listed. */
export const PLATFORM_PRESETS: PresetEntry[] = [
  { slug: 'web', labelEn: 'Web', labelZh: '网页', icon: Globe2, color: 'emerald' },
  { slug: 'ios', labelEn: 'iOS', labelZh: 'iOS', icon: Smartphone, color: 'sky' },
  { slug: 'android', labelEn: 'Android', labelZh: 'Android', icon: MonitorSmartphone, color: 'lime' },
  { slug: 'mac', labelEn: 'Mac', labelZh: 'Mac', icon: Laptop, color: 'violet' },
  { slug: 'windows', labelEn: 'Windows', labelZh: 'Windows', icon: Monitor, color: 'cyan' },
  { slug: 'linux', labelEn: 'Linux', labelZh: 'Linux', icon: Terminal, color: 'amber' },
  { slug: 'api', labelEn: 'API', labelZh: 'API', icon: Cable, color: 'fuchsia' },
  { slug: 'cli', labelEn: 'CLI', labelZh: 'CLI', icon: Braces, color: 'indigo' },
  { slug: 'mcp', labelEn: 'MCP', labelZh: 'MCP', icon: Bot, color: 'orange' },
  { slug: 'extension', labelEn: 'Extensions', labelZh: '扩展', icon: Puzzle, color: 'rose' },
];

/** Use-case tiles — cross-domain TASKS (a verb, not a domain label). These are
 *  the "分类+" layer: a video tool can belong to the Video category AND the
 *  Summarize / Edit tasks without the two dimensions ever overlapping. */
export const USE_CASE_PRESETS: PresetEntry[] = [
  { slug: 'generate', labelEn: 'Generate', labelZh: '生成', icon: Sparkles, color: 'violet' },
  { slug: 'edit', labelEn: 'Edit', labelZh: '编辑', icon: PenTool, color: 'sky' },
  { slug: 'summarize', labelEn: 'Summarize', labelZh: '总结', icon: FileText, color: 'amber' },
  { slug: 'automate', labelEn: 'Automate', labelZh: '自动化', icon: Workflow, color: 'cyan' },
  { slug: 'research', labelEn: 'Research', labelZh: '研究', icon: Search, color: 'emerald' },
  { slug: 'translate', labelEn: 'Translate', labelZh: '翻译', icon: Languages, color: 'indigo' },
  { slug: 'assist', labelEn: 'Assist', labelZh: '辅助', icon: Bot, color: 'rose' },
  { slug: 'analyze', labelEn: 'Analyze', labelZh: '分析', icon: BarChart3, color: 'orange' },
];

/** Audience personas — taxonomy rows (`taxonomy.type = 'audience'`). Names and
 *  translations live in the DB (`taxonomy` + `taxonomy_translation`); this meta
 *  only pins the rail/detail visual identity (icon + hue) per slug. Counts and
 *  translated labels arrive from `getHomeRail(locale)` (post_taxonomy
 *  aggregation). */
export type AudienceRailEntry = {
  slug: string;
  name: string;
  href: string;
  icon: LucideIcon;
  color: TileColor;
  count: number;
};

/** Visual identity for audience taxonomy slugs. Unknown slugs fall back to the
 *  muted pill (no icon). */
export const AUDIENCE_META: Record<string, { icon: LucideIcon; color: TileColor }> = {
  developers: { icon: Code2, color: 'emerald' },
  'saas-founders': { icon: Rocket, color: 'amber' },
  'seo-specialists': { icon: Search, color: 'sky' },
  'content-creators': { icon: Palette, color: 'violet' },
  designers: { icon: Brush, color: 'fuchsia' },
  marketers: { icon: Megaphone, color: 'orange' },
  'enterprise-teams': { icon: Users, color: 'sky' },
  'business-owners': { icon: BriefcaseBusiness, color: 'orange' },
  'educators-students': { icon: GraduationCap, color: 'indigo' },
  researchers: { icon: BarChart3, color: 'cyan' },
  'general-consumers': { icon: UserRound, color: 'violet' },
  freelancers: { icon: Laptop, color: 'cyan' },
  'product-managers': { icon: ListTodo, color: 'indigo' },
  'data-analysts': { icon: BarChart3, color: 'emerald' },
  writers: { icon: PenLine, color: 'amber' },
  'video-creators': { icon: Clapperboard, color: 'rose' },
  podcasters: { icon: Mic, color: 'fuchsia' },
  musicians: { icon: Music, color: 'amber' },
  photographers: { icon: Camera, color: 'sky' },
  translators: { icon: Languages, color: 'indigo' },
  journalists: { icon: Newspaper, color: 'rose' },
  'ecommerce-sellers': { icon: ShoppingCart, color: 'lime' },
  agencies: { icon: Building2, color: 'orange' },
  'sales-professionals': { icon: Handshake, color: 'emerald' },
  'hr-professionals': { icon: Users, color: 'violet' },
  'customer-support': { icon: LifeBuoy, color: 'sky' },
  'legal-professionals': { icon: Scale, color: 'violet' },
  'finance-professionals': { icon: Landmark, color: 'lime' },
  nocoders: { icon: Wrench, color: 'lime' },
  'job-seekers': { icon: Briefcase, color: 'cyan' },
};

export function audienceMeta(slug: string): { icon: LucideIcon; color: TileColor } | undefined {
  return AUDIENCE_META[slug];
}

export type RailCategory = {
  slug: string;
  /** DB-localized group name. */
  name: string;
  icon: LucideIcon;
  color: TileColor;
  count: number;
};

export type HomeRailModel = {
  /** Real taxonomy groups with live tool counts (empty ones dropped) for the left rail. */
  categories: RailCategory[];
  platforms: Array<PresetEntry & { count: number }>;
  useCases: Array<PresetEntry & { count: number }>;
  /** Audience taxonomy entries — DB-driven names, preset visuals, relation counts. */
  audiences: AudienceRailEntry[];
  alternatives: HomeRailData['alternatives'];
};

// ─── Slug → preset lookups (shared by homepage rails + item detail) ──────────
// The item-detail page reuses these colors and icons so the same taxonomy
// slug renders the same hue and glyph everywhere; unknown slugs fall back to
// the muted pill (no icon).

const PLATFORM_BY_SLUG = new Map(PLATFORM_PRESETS.map((p) => [p.slug, p]));
const SUB_CATEGORY_BY_SLUG = new Map(SUB_CATEGORY_PRESETS.map((p) => [p.slug, p]));
const CATEGORY_BY_SLUG = new Map(CATEGORY_PRESETS.map((p) => [p.slug, p]));

export function platformPreset(slug: string): PresetEntry | undefined {
  return PLATFORM_BY_SLUG.get(slug);
}

/** Sub-category slug → preset, falling back to its parent category's. */
export function subCategoryPreset(slug: string): PresetEntry | undefined {
  return SUB_CATEGORY_BY_SLUG.get(slug) ?? CATEGORY_BY_SLUG.get(slug);
}

/** Audience taxonomy slug → visual preset (see `AUDIENCE_META`). */
export function audiencePreset(slug: string): { icon: LucideIcon; color: TileColor } | undefined {
  return audienceMeta(slug);
}

export function platformColor(slug: string): TileColor | undefined {
  return platformPreset(slug)?.color;
}

export function subCategoryColor(slug: string): TileColor | undefined {
  return subCategoryPreset(slug)?.color;
}

export function audienceColor(slug: string): TileColor | undefined {
  return audienceMeta(slug)?.color;
}

/** Pick the label for the active locale (presets carry en/zh copy inline). */
export function presetLabel(entry: { labelEn: string; labelZh: string }, locale: string): string {
  return locale === 'zh' ? entry.labelZh : entry.labelEn;
}

/** Fallback when the rail data failed to load — renders the layout without tiles. */
export const EMPTY_RAIL_MODEL: HomeRailModel = {
  categories: [],
  platforms: [],
  useCases: [],
  audiences: [],
  alternatives: [],
};

/**
 * `groups` are the real taxonomy groups (`getGroups(locale)`, names already
 * localized); the rail links only to those and hides empty ones. Without them
 * (load failure) the category block is empty rather than guessing slugs.
 */
export function buildRailModel(
  data: HomeRailData,
  groups: ReadonlyArray<{ slug: string; name: string }> = [],
): HomeRailModel {
  return {
    categories: groups
      .map((group) => ({
        slug: group.slug,
        name: group.name,
        ...categoryLook(group.slug),
        count: data.categoryCounts[group.slug] ?? 0,
      }))
      .filter((category) => category.count > 0),
    platforms: PLATFORM_PRESETS.map((p) => ({ ...p, count: data.platformCounts[p.slug] ?? 0 })),
    useCases: USE_CASE_PRESETS.map((u) => ({ ...u, count: data.useCaseCounts[u.slug] ?? 0 })),
    audiences: data.audiences
      .map((a) => ({
        slug: a.slug,
        name: a.name,
        href: `/audiences/${a.slug}`,
        icon: AUDIENCE_META[a.slug]?.icon ?? Users,
        color: AUDIENCE_META[a.slug]?.color ?? 'sky',
        count: data.audienceCounts[a.slug] ?? 0,
      }))
      .sort((x, y) => y.count - x.count),
    alternatives: data.alternatives,
  };
}
